import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { getProduct, formatPrice } from '../lib/shopify';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ChevronLeft, Minus, Plus, Droplet, Shield, Truck, ArrowRight } from 'lucide-react';

const ProductDetailPage = () => {
  const { handle } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const { addItem, loading: cartLoading } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const data = await getProduct(handle);
        setProduct(data);
        if (data.variants?.length > 0) {
          setSelectedVariant(data.variants[0]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    if (handle) {
      fetchProduct();
    }
  }, [handle]);

  const handleAddToCart = async () => {
    if (selectedVariant?.id) {
      const productInfo = {
        title: product.title,
        variantTitle: selectedVariant.title,
        price: selectedVariant.price?.amount || product.price,
        image: product.images?.[0]?.url,
        handle: product.handle
      };
      const success = addItem(selectedVariant.id, quantity, productInfo);
      if (success) {
        toast.success('Added to cart!', {
          description: `${product.title} has been added to your cart.`
        });
      }
    }
  };

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  // Group variants by option name
  const variantOptions = {};
  product?.variants?.forEach(variant => {
    variant.selectedOptions?.forEach(option => {
      if (!variantOptions[option.name]) {
        variantOptions[option.name] = [];
      }
      if (!variantOptions[option.name].includes(option.value)) {
        variantOptions[option.name].push(option.value);
      }
    });
  });

  if (loading) {
    return (
      <main className="pt-20 min-h-screen bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse">
            <div className="h-4 bg-[#111] w-32 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-[#111]" />
              <div className="space-y-6">
                <div className="h-8 bg-[#111] w-3/4" />
                <div className="h-6 bg-[#111] w-1/4" />
                <div className="h-24 bg-[#111]" />
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-20 min-h-screen bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
          <h1 className="text-2xl text-white mb-4">Product not found</h1>
          <Link to="/jewellery" className="btn-luxury-outline">
            Back to Shop
          </Link>
        </div>
      </main>
    );
  }

  const currentImage = product.images?.[selectedImage];

  return (
    <>
      <Helmet>
        <title>{product.title} | LuxuryStyle</title>
        <meta name="description" content={product.description?.substring(0, 160)} />
        <meta property="og:title" content={`${product.title} | LuxuryStyle`} />
        <meta property="og:description" content={product.description?.substring(0, 160)} />
        <meta property="og:image" content={currentImage?.url} />
        <meta property="og:type" content="product" />
        <link rel="canonical" href={`https://luxurystyle.info/product/${handle}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.title,
            "description": product.description,
            "image": product.images?.map(img => img.url),
            "offers": {
              "@type": "Offer",
              "price": product.price,
              "priceCurrency": product.currency,
              "availability": product.availableForSale 
                ? "https://schema.org/InStock" 
                : "https://schema.org/OutOfStock"
            }
          })}
        </script>
      </Helmet>

      <main data-testid="product-detail-page" className="pt-20 min-h-screen bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          {/* Breadcrumb */}
          <Link 
            to="/jewellery" 
            data-testid="back-to-shop"
            className="inline-flex items-center space-x-2 text-white/60 hover:text-[#D4AF37] transition-colors mb-8"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="font-accent text-xs uppercase tracking-[0.2em]">Back to Shop</span>
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-square bg-[#111] overflow-hidden">
                {currentImage && (
                  <img
                    src={currentImage.url}
                    alt={currentImage.altText || product.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                )}
                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col space-y-2">
                  <span className="badge-waterproof flex items-center space-x-1">
                    <Droplet className="w-3 h-3" />
                    <span>Waterproof</span>
                  </span>
                  <span className="badge-tarnish-free flex items-center space-x-1">
                    <Shield className="w-3 h-3" />
                    <span>Tarnish-Free</span>
                  </span>
                </div>
              </div>

              {/* Thumbnail Gallery */}
              {product.images?.length > 1 && (
                <div className="flex space-x-3 overflow-x-auto pb-2">
                  {product.images.map((image, index) => (
                    <button
                      key={image.id || index}
                      onClick={() => setSelectedImage(index)}
                      data-testid={`thumbnail-${index}`}
                      className={`flex-shrink-0 w-20 h-20 border-2 transition-colors ${
                        selectedImage === index 
                          ? 'border-[#D4AF37]' 
                          : 'border-transparent hover:border-white/20'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.altText || `${product.title} view ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              {/* Title & Price */}
              <div>
                <h1 className="text-3xl md:text-4xl font-medium text-white mb-4">
                  {product.title}
                </h1>
                <p className="text-2xl text-[#D4AF37]">
                  {formatPrice(selectedVariant?.price?.amount || product.price, product.currency)}
                </p>
              </div>

              {/* Description */}
              {product.description && (
                <div className="prose prose-invert prose-sm max-w-none">
                  <p className="text-white/60 leading-relaxed">{product.description}</p>
                </div>
              )}

              <div className="gold-divider-left" />

              {/* Variant Options */}
              {Object.entries(variantOptions).map(([optionName, values]) => (
                <div key={optionName}>
                  <label className="block text-white/60 text-xs uppercase tracking-[0.2em] mb-3">
                    {optionName}
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {values.map((value) => {
                      const variant = product.variants?.find(v => 
                        v.selectedOptions?.some(o => o.name === optionName && o.value === value)
                      );
                      const isSelected = selectedVariant?.selectedOptions?.some(
                        o => o.name === optionName && o.value === value
                      );
                      
                      return (
                        <button
                          key={value}
                          onClick={() => variant && setSelectedVariant(variant)}
                          data-testid={`variant-${optionName}-${value}`}
                          className={`size-option ${isSelected ? 'selected' : ''} ${
                            !variant?.availableForSale ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={!variant?.availableForSale}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Quantity */}
              <div>
                <label className="block text-white/60 text-xs uppercase tracking-[0.2em] mb-3">
                  Quantity
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    data-testid="decrease-quantity"
                    className="quantity-btn"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-white text-lg w-12 text-center">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    data-testid="increase-quantity"
                    className="quantity-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <button
                onClick={handleAddToCart}
                disabled={cartLoading || !selectedVariant?.availableForSale}
                data-testid="add-to-cart"
                className="btn-luxury w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>
                  {cartLoading 
                    ? 'Adding...' 
                    : selectedVariant?.availableForSale 
                      ? 'Add to Cart' 
                      : 'Sold Out'
                  }
                </span>
                {!cartLoading && selectedVariant?.availableForSale && (
                  <ArrowRight className="w-4 h-4" />
                )}
              </button>

              {/* Shipping Info */}
              <div className="flex items-start space-x-3 p-4 bg-[#111] border border-white/5">
                <Truck className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-white text-sm font-medium">Free Shipping</p>
                  <p className="text-white/60 text-xs">On all orders over £22</p>
                </div>
              </div>

              {/* Product Features */}
              <div className="space-y-3 pt-4">
                <div className="flex items-center space-x-3 text-white/60 text-sm">
                  <Droplet className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  <span>100% Waterproof - Wear in shower, pool, or ocean</span>
                </div>
                <div className="flex items-center space-x-3 text-white/60 text-sm">
                  <Shield className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  <span>Tarnish-Free - Keeps its shine for years</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ProductDetailPage;
